// Right-docked compare panel — P3 (Design Spec §9 "Compare (2–3 models)").
// BotWidget keeps this mounted alongside BotPanel at all times; only one of
// the two is ever visually shown, gated internally off botStore.viewMode
// (`shellVisible` below) so a live popover<->dock swap plays each shell's
// own enter/exit transition instead of an instant unmount+remount. Both
// share the same `isOpen` state (see BotWidget.tsx). This file owns
// everything specific to the dock; AnswerCard/BotComposer/MemoryTab/
// SettingsTab are reused, never duplicated.

import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence, useReducedMotion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { GlassPill, Dropdown, Button, EmptyState, Segmented, Badge, Accordion, type DropdownOption } from '@/shared/ui'
import { useBotStore, type BotTab } from './botStore'
import { useBotConversations, useBotConversation, useMonthUsage, usePatchSettings, usePatchConversation } from './hooks/useBot'
import { isTerminal, formatEur } from './botUtils'
import { AnswerCard } from './AnswerCard'
import { BotComposer } from './BotComposer'
import { CHROME_SPRING } from './botMotion'
import MemoryTab from './MemoryTab'
import SettingsTab from './SettingsTab'
import ReportTab from './ReportTab'

// Badge tone per bot.Answer.status — duplicated from AnswerCard.tsx (not
// exported there, and that file isn't in this build's edit scope). Kept as
// the identical literal map so the two chip renderings never drift.
const STATUS_TONE = {
  queued: 'neutral',
  running: 'info',
  done: 'success',
  failed: 'danger',
  cancelled: 'neutral',
} as const

/** Sidebar width in px at the `lg` breakpoint: the sidebar is always the 72 px icon bar
 *  (hover/toggle opens it as an overlay and never moves the page), so the page keeps
 *  router.tsx's `lg:ml-[104px]`. Below `lg` the dock goes full-width. */
const SIDEBAR_WIDTH_PX = 104

/** JS-measured viewport width, refreshed on resize. Used instead of the CSS
 *  `vw` unit for `--bot-dock-w`: `vw` resolves against the initial
 *  containing block's pre-scrollbar width, which is wider than the actually
 *  rendered viewport whenever the browser reserves non-overlay scrollbar
 *  space (the Windows Chrome/Firefox/Edge default) — the classic "100vw
 *  causes horizontal scroll" trap for a full-bleed `position: fixed`
 *  element. `window.innerWidth` reflects the real rendered viewport. */
function useViewportWidth(): number {
  const [width, setWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280))
  useEffect(() => {
    function onResize() {
      setWidth(window.innerWidth)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return width
}

type DockStyle = CSSProperties & { '--bot-dock-w'?: string }

export function BotCompareDock() {
  const { t } = useTranslation('bot')
  const reduced = useReducedMotion()
  const viewportWidth = useViewportWidth()

  const isOpen = useBotStore((s) => s.isOpen)
  const viewMode = useBotStore((s) => s.viewMode)
  const closeWidget = useBotStore((s) => s.close)
  const activeConversationId = useBotStore((s) => s.activeConversationId)
  const setConversation = useBotStore((s) => s.setConversation)
  const catchUpSeen = useBotStore((s) => s.catchUpSeen)
  const activeTab = useBotStore((s) => s.activeTab)
  const setActiveTab = useBotStore((s) => s.setActiveTab)
  const setViewMode = useBotStore((s) => s.setViewMode)
  const draft = useBotStore((s) => s.draft)
  const setDraft = useBotStore((s) => s.setDraft)
  // Header dock toggle (owner feedback round 3 §1): the ONE persisted
  // compare-layout control (Settings' old Segmented is gone). Reuses the
  // same settings-patch mutation SettingsTab used to — never a hand-rolled
  // fetch — so the invalidation-on-success behavior stays in one place.
  const patchSettings = usePatchSettings()

  // BotWidget keeps both shells mounted at all times (so a live viewMode
  // swap plays each shell's own enter/exit instead of an instant
  // unmount+remount) — `shellVisible` is "this shell is the one actually on
  // screen", distinct from the store's `isOpen` ("the widget is open at
  // all, in whichever shell"). Data-fetch `enabled` flags below stay on
  // bare `isOpen` (shared query keys with BotPanel — react-query dedupes),
  // but anything about THIS shell's own visible chrome (AnimatePresence,
  // Escape, focus) must key off `shellVisible` so the hidden shell doesn't
  // fight the visible one for Escape/focus.
  const shellVisible = isOpen && viewMode === 'dock'

  // Same enabled-gating as BotPanel — this component is always mounted (only
  // AnimatePresence's visibility gate below cares about `shellVisible`), so
  // these must not fetch for every user on every page load.
  const { data: conversations } = useBotConversations({ enabled: isOpen })
  const patchConversation = usePatchConversation()
  const { data: conversation } = useBotConversation(activeConversationId)
  const { data: monthUsage, isError: usageError } = useMonthUsage({ enabled: isOpen })

  const dockRef = useRef<HTMLDivElement>(null)
  const tabPanelRef = useRef<HTMLDivElement>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  const wasOpenRef = useRef(false)

  // Escape returns to the popover shell — it neither closes the widget nor
  // cancels in-flight work (same non-cancelling contract as BotPanel's own
  // Escape handler). Gated on `shellVisible` (not bare `isOpen`) since both
  // shells stay mounted — otherwise this listener and BotPanel's would both
  // be armed whenever the widget is open, regardless of which is showing.
  useEffect(() => {
    if (!shellVisible) return
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      // Bail when focus is inside some OTHER subtree (e.g. the conversation
      // Dropdown's portaled search/menu, which lives outside this subtree)
      // — otherwise this handler and the Dropdown's own Escape listener both
      // fire on the same keypress, and Escape kicks the whole dock back to
      // the popover instead of just dismissing the menu. `document.body`
      // (and `null`) count as "inside": when the Dropdown's portaled menu
      // unmounts on its own Escape handling, the browser drops focus to
      // `body` for an instant, and treating that as "outside" would leave
      // this Escape handler permanently dead until the user manually
      // refocuses something inside the dock.
      const active = document.activeElement
      if (dockRef.current && active && active !== document.body && !dockRef.current.contains(active)) return
      setViewMode('popover')
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [shellVisible, setViewMode])

  // Focus enters the dock whenever it becomes the visible shell (fresh
  // mount with isOpen already true — switching from the popover via
  // "Vergleich" — or the ordinary closed->open transition). Restoring focus
  // to the launcher only happens on a genuine close (isOpen true->false),
  // never on a live dock->popover swap, where isOpen stays true and
  // BotPanel's own focus effect takes over instead.
  useEffect(() => {
    if (shellVisible) {
      previouslyFocusedRef.current = document.activeElement as HTMLElement | null
      dockRef.current?.focus()
    } else if (!isOpen && wasOpenRef.current) {
      const launcherButton = document.getElementById('bot-launcher-button')
      if (launcherButton) launcherButton.focus()
      else previouslyFocusedRef.current?.focus()
    }
    wasOpenRef.current = isOpen
  }, [shellVisible, isOpen])

  // Same unread-badge catch-up as BotPanel (duplicated — BotPanel isn't in
  // this file's scope to share it from) so answers finishing while the dock
  // is the open shell don't inflate the badge. activeTab !== 'chat' guard
  // (Contract A fix): Memory/Settings can be showing while the dock stays
  // open on this conversation, in which case the user hasn't actually seen
  // the answer — mirrors TurnWatcher's own `viewing` check in BotWidget.tsx.
  useEffect(() => {
    if (!isOpen || !conversation || activeTab !== 'chat') return
    const visibleAnswerIds = conversation.turns.flatMap((turn) =>
      turn.answers.filter((a) => isTerminal(a.status)).map((a) => a.answerId),
    )
    if (visibleAnswerIds.length > 0) catchUpSeen(visibleAnswerIds)
  }, [isOpen, conversation, activeTab, catchUpSeen])

  // Design Spec §9: "same question pinned on top" / "Reopen -> scrolls to
  // the turn." Older turns render collapsed ABOVE the pinned latest turn
  // (Contract B), so without this the dock's initial scroll position shows
  // the older-turn history, not the comparison the user just triggered —
  // scrolling the tabpanel all the way down brings the latest turn's
  // sticky-pinned question to the top of the visible area. Mirrors
  // BotPanel's identical bottom-scroll effect.
  useEffect(() => {
    const el = tabPanelRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [shellVisible, conversation?.turns.length])

  const conversationOptions: DropdownOption[] = (conversations ?? []).map((c) => ({
    value: String(c.id),
    label: c.title ?? new Date(c.updatedAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
    note: c.updatedAt ? new Date(c.updatedAt).toLocaleDateString('de-DE') : undefined,
  }))

  // See BotPanel.tsx's identical comment: {{mine}} is pinned to the org-wide
  // total per Design Spec §13.4, not MonthUsage.mine.
  const spentEur = (monthUsage?.monthSpendMicroEur ?? 0) / 1_000_000
  const capEur = monthUsage?.capEur ?? 0
  const usageLabel = t('panel.monthUsage', { mine: formatEur(spentEur, 2), cap: formatEur(capEur, 0) })

  // Live switch is instant; the PATCH that persists it as the device's
  // default is best-effort and fire-and-forget — a failed PATCH neither
  // reverts the live view nor blocks the UI (worst case: the old default
  // shows again on the next device/reload). `isPending`-gated so a rapid
  // double click can't fire two overlapping PATCHes.
  function handleBackToPopover() {
    if (patchSettings.isPending) return
    setViewMode('popover')
    patchSettings.mutate({ compareLayout: 'popover' })
  }

  const examples = (() => {
    const raw = t('empty.examples', { returnObjects: true })
    return Array.isArray(raw) ? (raw as string[]) : []
  })()

  const turns = conversation?.turns ?? []
  const latestTurn = turns.length > 0 ? turns[turns.length - 1] : undefined
  const olderTurns = turns.length > 1 ? turns.slice(0, -1) : []

  // JS-measured, not `100vw` — see useViewportWidth's comment. Only applies
  // at the `lg` breakpoint (className below), where the dock hangs off the
  // page's right edge next to the sidebar; below `lg` the dock goes
  // full-bleed via `inset-0` instead of an explicit width.
  const sidebarWidth = SIDEBAR_WIDTH_PX
  const dockWidthPx = Math.min(1200, Math.max(0, viewportWidth - sidebarWidth))
  const dockStyle: DockStyle = { '--bot-dock-w': `${dockWidthPx}px` }

  return createPortal(
    <AnimatePresence>
      {shellVisible && (
        <motion.div
          ref={dockRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="false"
          aria-labelledby="bot-dock-title"
          style={dockStyle}
          // Below `lg`: `inset-0` (all four sides pinned) solves the box's
          // width from the real containing block, not `vw` — avoids the
          // scrollbar-width mismatch `w-screen`/`100vw` would introduce on a
          // fixed full-bleed element (see useViewportWidth's comment). At
          // `lg`+: `left-auto` releases the left edge so `right-0` + the
          // JS-measured `--bot-dock-w` position/size the panel instead.
          className="fixed z-[9997] inset-0 lg:left-auto lg:w-[var(--bot-dock-w)] outline-none"
          initial={reduced ? false : { opacity: 0, x: '100%' }}
          animate={{ opacity: 1, x: 0, transition: reduced ? { duration: 0 } : CHROME_SPRING }}
          // Instant exit for the same reason as BotPanel's own exit: the
          // launcher only reappears once isOpen goes false, and its
          // bottom-right hit box sits inside this dock's own footprint (the
          // dock spans the full right edge). An animated exit would leave
          // both interactive over that corner for the spring's duration.
          exit={{ opacity: 0, x: reduced ? 0 : '100%', transition: { duration: 0 } }}
        >
          {/* Full-height right-docked panel: a page-sized data surface, so the shell is
              SOLID (DESIGN.md §14 — glass only frames data; stacked glass over the app's
              own glass header let the header bleed through the dock chrome). */}
          <div className="flex flex-col h-full overflow-hidden bg-surface-container-lowest border-l border-outline-variant shadow-2xl">
            {/* Header — glass chrome, two rows. Row 1 (title/Dropdown/Neu/
                month chip/close) is a single non-wrapping row so the close
                button always has a guaranteed, reachable position — the
                Dropdown (min-w-0 flex-1) absorbs the squeeze, "Neu" drops to
                icon-only and the month chip disappears entirely once the
                dock gets too narrow for all of them (mirrors BotPanel.tsx's
                identical header). Row 2 (tabs/toggle) sits independently
                below — a single 7-element row here previously let the tabs
                push × into overflow below the `lg` breakpoint. */}
            <div className="flex flex-col gap-2 p-3 border-b border-outline-variant">
              {/* Restrained cascade reveal on shell open — 40ms stagger per
                  row/body/composer, spring physics via CHROME_SPRING
                  (DESIGN.md §14 "cascade reveals on mount"). Each motion.div
                  only replays its `initial`->`animate` on a fresh mount of
                  this shell (AnimatePresence unmounts the whole tree on
                  close), never on ordinary re-renders while open. */}
              <motion.div
                className="flex items-center gap-2"
                initial={reduced ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0, transition: reduced ? { duration: 0 } : CHROME_SPRING }}
              >
                <h2 id="bot-dock-title" className="font-headline font-bold text-on-surface text-sm mr-1 flex-shrink-0">
                  {t('dock.title')}
                </h2>
                <div className="min-w-0 max-w-sm flex-1">
                  <Dropdown
                    value={activeConversationId != null ? String(activeConversationId) : ''}
                    options={conversationOptions}
                    onChange={(v) => setConversation(v ? Number(v) : null)}
                    placeholder={conversationOptions.length > 0 ? t('panel.newConversationPlaceholder') : t('panel.noConversations')}
                    size="compact"
                    variant="glass"
                    usePortal
                    menuZIndex={9998}
                    searchable
                  />
                </div>
                <Button variant="ghost" size="sm" icon="add" aria-label={t('panel.newConversation')} onClick={() => setConversation(null)}>
                  <span className="hidden sm:inline">{t('panel.newConversation')}</span>
                </Button>
                {/* "Löschen" = archive (owner ask 2026-09-01): the row leaves
                    the picker, but turns/usage stay — the cost ledger in the
                    Bericht must never lose rows. No confirm dialog: the
                    action is server-side reversible (archived flag). */}
                {activeConversationId != null && (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon="delete"
                    aria-label={t('panel.deleteConversation')}
                    title={t('panel.deleteConversation')}
                    disabled={patchConversation.isPending}
                    onClick={() =>
                      patchConversation.mutate(
                        { id: activeConversationId, body: { archived: true } },
                        { onSuccess: () => setConversation(null) },
                      )
                    }
                  />
                )}
                {/* Hidden while GET /api/bot/usage/month has failed or
                    hasn't resolved yet — an unpriced "0,00 € / 0 €" chip
                    would misreport spend as zero rather than unknown. Also
                    hidden under a ~440px dock width (see BotPanel.tsx's
                    identical comment for the 464px viewport math — the dock
                    goes full-bleed below `lg`, same as the popover below
                    `sm`). */}
                {monthUsage && !usageError && (
                  <GlassPill className="hidden min-[464px]:block px-3 py-1.5 text-xs text-on-surface-variant whitespace-nowrap tabular-nums">
                    {usageLabel}
                  </GlassPill>
                )}
                <Button variant="ghost" size="sm" icon="close" aria-label={t('panel.close')} onClick={closeWidget} />
              </motion.div>
              <motion.div
                className="flex items-center gap-2 flex-wrap"
                initial={reduced ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0, transition: reduced ? { duration: 0 } : { ...CHROME_SPRING, delay: 0.04 } }}
              >
                <Segmented<BotTab>
                  value={activeTab}
                  onChange={setActiveTab}
                  size="sm"
                  options={[
                    { value: 'chat', icon: 'chat', label: t('tabs.chat') },
                    { value: 'memory', icon: 'psychology', label: t('tabs.memory') },
                    { value: 'report', icon: 'receipt_long', label: t('tabs.report') },
                    { value: 'settings', icon: 'settings', label: t('tabs.settings') },
                  ]}
                />
                {/* Icon-only — this IS the persisted compare-layout control
                    now (owner feedback round 3 §1: the Settings section is
                    gone). aria-pressed is always true here: this shell only
                    renders while viewMode === 'dock' (shellVisible above), so
                    the dock state this button toggles off is always the
                    current one — BotPanel's own copy carries `false`. */}
                <Button
                  variant="ghost"
                  size="sm"
                  icon="close_fullscreen"
                  aria-label={t('dock.toPopover')}
                  aria-pressed={true}
                  title={t('dock.toPopover')}
                  disabled={patchSettings.isPending}
                  onClick={handleBackToPopover}
                />
              </motion.div>
            </div>

            {/* Body — flat data surface, DESIGN.md §14. `role="tabpanel"` +
                `aria-label` complete the tablist/tabpanel relationship the
                header's Segmented (role="tablist"/"tab") declares — an
                id/aria-labelledby pair isn't possible here since Segmented
                doesn't expose per-option ids to link to. */}
            <motion.div
              ref={tabPanelRef}
              className="flex-1 overflow-y-auto bg-surface-container-lowest p-4"
              initial={reduced ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0, transition: reduced ? { duration: 0 } : { ...CHROME_SPRING, delay: 0.08 } }}
              role="tabpanel"
              id={`bot-dock-tabpanel-${activeTab}`}
              aria-label={t(`tabs.${activeTab}`)}
            >
              {activeTab === 'memory' ? (
                <MemoryTab />
              ) : activeTab === 'report' ? (
                <ReportTab />
              ) : activeTab === 'settings' ? (
                <SettingsTab />
              ) : !conversation || turns.length === 0 ? (
                <EmptyState
                  icon="chat"
                  title={t('empty.title')}
                  description={t('empty.subtitle')}
                  action={
                    examples.length > 0 ? (
                      <div className="flex flex-wrap gap-2 justify-center">
                        {examples.map((example, i) => (
                          <Button key={i} variant="secondary" size="sm" onClick={() => setDraft(example)}>
                            {example}
                          </Button>
                        ))}
                      </div>
                    ) : undefined
                  }
                />
              ) : (
                <div className="space-y-4">
                  {olderTurns.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-on-surface-variant uppercase tracking-wide mb-2">
                        {t('dock.olderTurns')}
                      </div>
                      {/* No reduced-motion handling here — Accordion's own
                          expand/collapse transition is unconditional
                          (shared/ui/Accordion.tsx), outside this file's edit
                          scope. Known gap for a follow-up in that component. */}
                      <Accordion
                        items={olderTurns.map((turn) => ({
                          id: turn.turnId,
                          header: (
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-sm text-on-surface truncate flex-1">{turn.question}</span>
                              <div className="flex gap-1 flex-shrink-0">
                                {turn.answers.map((a) => (
                                  <span key={a.answerId} title={t(`status.${a.status}`)}>
                                    <Badge tone={STATUS_TONE[a.status]} size="sm">{a.modelLabel}</Badge>
                                  </span>
                                ))}
                              </div>
                            </div>
                          ),
                          content: (
                            <div className="overflow-x-auto">
                              <div
                                className="grid gap-3"
                                style={{ gridTemplateColumns: `repeat(${Math.max(1, turn.answers.length)}, minmax(360px, 1fr))` }}
                              >
                                {turn.answers.map((a) => (
                                  <AnswerCard key={a.answerId} answer={a} />
                                ))}
                              </div>
                            </div>
                          ),
                        }))}
                      />
                    </div>
                  )}

                  {latestTurn && (
                    <div>
                      {/* Pinned: stays visible while the columns below scroll,
                          so the question being compared never leaves view.
                          No backdrop-blur — this body is a flat data surface
                          (DESIGN.md §14); the near-opaque /95 background
                          alone keeps the scrolling columns underneath from
                          bleeding through. */}
                      <div className="sticky top-0 z-10 -mx-4 px-4 pt-1 pb-3 bg-surface-container-lowest/95">
                        <div className="flex justify-end">
                          <div className="max-w-[85%] rounded-2xl rounded-br-md bg-primary text-on-primary px-4 py-2.5 text-sm whitespace-pre-wrap">
                            {latestTurn.question}
                          </div>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <div
                          className="grid gap-3"
                          style={{ gridTemplateColumns: `repeat(${Math.max(1, latestTurn.answers.length)}, minmax(360px, 1fr))` }}
                        >
                          {latestTurn.answers.map((answer) => (
                            <AnswerCard key={answer.answerId} answer={answer} />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>

            {activeTab === 'chat' && (
              <motion.div
                initial={reduced ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0, transition: reduced ? { duration: 0 } : { ...CHROME_SPRING, delay: 0.12 } }}
              >
                <BotComposer conversationId={activeConversationId} value={draft} onValueChange={setDraft} />
              </motion.div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
